import { useQuery } from '@tanstack/react-query'
import { Alert } from '../components/ui/feedback.js'
import { Button } from '../components/ui/button.js'
import { t } from '../i18n/index.js'
import { API_URL } from '../lib/api.js'

interface HealthResponse {
  status: 'ok' | 'degraded'
  version: string
  gitSha: string
  environment: string
  checks: { database: { status: 'ok' | 'error'; latencyMs: number | null } }
}

/**
 * Shows which build the API is running.
 *
 * Deliberately unauthenticated and deliberately kept: right after a deploy, the
 * first question is always which commit is actually live.
 */
export function StatusPage() {
  const strings = t()

  const query = useQuery({
    queryKey: ['health'],
    queryFn: async (): Promise<HealthResponse> => {
      const response = await fetch(`${API_URL}/health`)
      return (await response.json()) as HealthResponse
    },
    retry: false,
  })

  return (
    <div className="flex max-w-xl flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold">{strings.status.heading}</h1>
      </div>

      {query.isPending && <p className="text-text-muted text-sm">{strings.status.checking}</p>}

      {query.isError && <Alert tone="danger">{strings.status.offline}</Alert>}

      {query.data && (
        <>
          <Alert tone={query.data.status === 'ok' ? 'success' : 'warning'}>
            {query.data.status === 'ok' ? strings.status.online : strings.status.offline}
          </Alert>

          <dl className="border-border divide-border divide-y rounded-lg border text-sm">
            <div className="flex justify-between px-4 py-2.5">
              <dt className="text-text-muted">{strings.status.version}</dt>
              <dd>{query.data.version}</dd>
            </div>
            <div className="flex justify-between gap-4 px-4 py-2.5">
              <dt className="text-text-muted">{strings.status.commit}</dt>
              <dd className="truncate font-mono text-xs">{query.data.gitSha}</dd>
            </div>
            <div className="flex justify-between px-4 py-2.5">
              <dt className="text-text-muted">{strings.status.environment}</dt>
              <dd>{query.data.environment}</dd>
            </div>
            <div className="flex justify-between px-4 py-2.5">
              <dt className="text-text-muted">{strings.status.database}</dt>
              <dd>
                {query.data.checks.database.status === 'ok'
                  ? strings.status.databaseOk
                  : strings.status.databaseError}
              </dd>
            </div>
          </dl>
        </>
      )}

      <div>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {strings.status.retry}
        </Button>
      </div>
    </div>
  )
}
